import DetailsPageTitle from "components/common/DetailsPageTitle";
import React, { useEffect, useState } from "react";
import { Col, Container, Row, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import {
  Calendar,
  Clock4,
  DollarSign,
  Download,
  MapPin,
  User,
} from "lucide-react";
import NoDataFound from "components/common/NoDataFound";
import PDFIcon from "assets/images/icons/pdf.svg";
import Riyal from "assets/images/icons/riyal.svg";
import riyalGray2 from "assets/images/icons/riyalGray2.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  getWorkshopInvoice,
  getWorkspaceDetails,
} from "store/actions/workspaceSlice";
import Spinners from "components/common/Spinners";
import { formatDate, getTimeDifference } from "components/common/Utils";
import { showAlert } from "store/reducers/alertReducer";
import primaryRiyal from "assets/images/icons/primaryRiyal.svg";

const WorkshopDetails = () => {
  const { t } = useTranslation();
  document.title = t("Workshop Details | Work Space");

  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const dispatch = useDispatch();

  const {
    loading,
    isLoading,
    workspaceDetails: workshopDetails,
    workshopInvoiceDetails,
  } = useSelector((state) => state.Workspace);

  const [isInvoiceGenerated, setIsInvoiceGenerated] = useState(false);

  useEffect(() => {
    const getDetails = () => {
      const payload = {
        id,
      };
      dispatch(getWorkspaceDetails(payload));
    };
    getDetails();
  }, []);

  const handleDownloadInvoice = () => {
    const payload = {
      id: id || workshopDetails?._id,
    };

    dispatch(getWorkshopInvoice(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          setIsInvoiceGenerated(true);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
          setIsInvoiceGenerated(false);
        }
      })
      .catch((err) => {});
  };

  return (
    <React.Fragment>
      <div className="page-content common_details_page workshop_details_page">
        {loading ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <DetailsPageTitle title={t("Workshop Details")} />
            </Row>
            <div className="common_wrapper">
              <Row>
                <Col lg={7}>
                  <Row>
                    {/* IMAGES ROW */}
                    <Col lg={12} className="images_section">
                      <div className="d-flex gap-3">
                        <div className="left_section">
                          {workshopDetails?.imageUrls?.[0] && (
                            <img
                              src={workshopDetails.imageUrls[0]}
                              alt="Main Image"
                            />
                          )}
                        </div>
                        <div
                          className="right_section"
                          style={{
                            gridTemplateColumns:
                              workshopDetails?.imageUrls?.length <= 3
                                ? "1fr"
                                : "repeat(2, 1fr)",
                          }}
                        >
                          {workshopDetails?.imageUrls
                            ?.slice(1)
                            .map((url, index) => (
                              <img
                                key={index}
                                src={url}
                                alt={`Workspace Image ${index + 2}`}
                              />
                            ))}
                        </div>
                      </div>
                    </Col>
                    {/* TITLE ROW */}
                    <Col lg={12} className="mt_20">
                      <div className="g_between">
                        <h2 className="fs_24">
                          {workshopDetails?.name},{" "}
                          {workshopDetails?.nameAr || "-"}
                        </h2>
                        <p className="available_tag">
                          {workshopDetails?.bookingDetails
                            ? workshopDetails?.workShopDetails?.numberOfSeats -
                              workshopDetails?.bookingDetails?.seats
                            : workshopDetails?.workShopDetails
                                ?.numberOfSeats}{" "}
                          {t("Available")}
                        </p>
                      </div>
                      {/* <p className="text_danger d-flex align-items-center gap-2 mt_10">
                        <span className="circle"></span>{" "}
                        <Hourglass
                          className="text_danger"
                          width={15}
                          height={15}
                        />{" "}
                        {getRemainingDaysForWorkshop(
                          workshopDetails?.selectedDateAndTimes
                        )}
                        2 Day Left !
                      </p> */}
                      <p className="fs_14 text_dark_gray d-flex gap-3 align-items-center flex-wrap mt_15">
                        <span className="g_center gap-1">
                          <MapPin width={14} height={14} />
                          {workshopDetails?.cityId?.name || "-"}
                        </span>
                        {/* <span className="g_center gap-1">
                          <Clock4 width={14} height={14} />
                          6:00pm - 9:00pm
                        </span>
                        <span className="g_center gap-1">
                          <Calendar width={14} height={14} />
                          2025, Jan 01
                        </span> */}
                        <span className="g_center gap-1">
                          <User width={14} height={14} />
                          {workshopDetails?.workShopDetails?.numberOfSeats}{" "}
                          {t("Seats")}
                        </span>
                        <span className="g_center gap-1">
                          {/* <DollarSign width={14} height={14} /> */}
                          <img
                            src={riyalGray2}
                            width={15}
                            height={15}
                            alt="riyal"
                            className=""
                          />
                          {workshopDetails?.workShopDetails?.price}{" "}
                        </span>
                        <span className="g_center gap-1">
                          <User width={14} height={14} />
                          {workshopDetails?.createdBy || "-"}
                        </span>
                      </p>
                      <h2 className="fs_20 mt_20">{t("Date and Time")}</h2>
                      <div className="date_time_row">
                        <div className="time_line"></div>
                        {workshopDetails?.selectedDateAndTimes?.length === 0 ? (
                          <p>{t("No data found")}</p>
                        ) : (
                          workshopDetails?.selectedDateAndTimes?.map((item) => (
                            <div className="d-flex gap-3" key={item?._id}>
                              <div className="date_col">
                                <Calendar width={14} height={14} />{" "}
                                <span>{formatDate(item?.startDate)}</span>
                              </div>
                              <div className="date_time">
                                <Clock4 width={14} height={14} />{" "}
                                <span>
                                  (
                                  {getTimeDifference(
                                    item?.startTime?.start,
                                    item?.startTime?.end
                                  )}
                                  h) {item?.startTime?.start || "-"} -{" "}
                                  {item?.startTime?.end || "-"}
                                </span>
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      <h2 className="fs_20 mt_20">{t("Description")}</h2>
                      <div className="description_section">
                        <p className="fs_14">
                          {workshopDetails?.description || "-"}
                        </p>
                        <p className="fs_14 mt-4 rtl">
                          {workshopDetails?.descriptionAr || "-"}
                        </p>
                      </div>
                      <h2 className="fs_20 mt_20">{t("Topics")}</h2>
                      <div className="description_section">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: workshopDetails?.topics,
                          }}
                        />
                      </div>
                    </Col>
                    <Col lg={12} className="mt_20">
                      <div className="amenities_row">
                        <h2 className="fs_20">{t("Amenities")}</h2>
                        <div className="amenities_inner">
                          {workshopDetails?.amenities?.length === 0 ? (
                            <div className="b_r_20 bg-white w-100 pb-3">
                              <NoDataFound className="h_fit_content" />
                            </div>
                          ) : (
                            workshopDetails?.amenities?.map((amenity) => (
                              <div
                                className="single_amenity"
                                key={amenity?._id}
                              >
                                <img
                                  src={amenity?.imageUrl}
                                  alt={amenity?.name}
                                  width={24}
                                  height={24}
                                />
                                <p className="fs_14">{amenity.name || "-"}</p>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Col>
                {/* <Col lg={1}></Col> */}
                <Col lg={5}>
                  <div className="right_section ms-3 ms-xxl-4">
                    {/* WORKSHOP REVENUE ROW */}
                    <h2 className="fs_20">
                      {t("Workshop Revenue Distribution")}
                    </h2>
                    {workshopDetails?.bookingDetails ? (
                      <div>
                        <div className="revenue_section">
                          <div className="inner_section workshop_fee">
                            <div className="g_between align-items-start gap-4">
                              <p className="fs_14 text_gray">
                                {t("Workshop fee with Tax:")}
                              </p>
                              <p className="fs_14 text_gray ps-4">
                                {t("Seats")}
                              </p>
                              <p className="fs_14 text_gray">
                                {t("Total with Tax")}
                              </p>
                            </div>
                            <div className="g_between mt-1">
                              <h2 className="fs_20 text_black">
                                <img
                                  src={Riyal}
                                  width={15}
                                  height={15}
                                  alt="riyal"
                                  className="me-1"
                                />
                                {
                                  workshopDetails?.bookingDetails
                                    ?.workShopFeeWithTax
                                }{" "}
                              </h2>
                              <h2 className="fs_20 text_black d-flex ps-3 gap-3 text-center">
                                <span className="text_primary">X</span>
                                {workshopDetails?.bookingDetails?.seats}
                                <span className="text_primary">=</span>
                              </h2>
                              <h2 className="fs_28 text_primary ps-3">
                                <img
                                  src={primaryRiyal}
                                  width={20}
                                  height={20}
                                  alt="riyal"
                                  className="me-1"
                                />
                                {workshopDetails?.bookingDetails
                                  ?.totalWithTax || "-"}{" "}
                              </h2>
                            </div>
                          </div>
                          <div className="inner_section">
                            <div className="g_between">
                              <h2 className="fs_14 text_gray">
                                {t("Trainer's share")} (
                                {workshopDetails?.workShopDetails?.trainerShare}
                                %)
                              </h2>
                              <h2 className="fs_28 text_primary">
                                {/* {calculateVAT(
                              totalPrice,
                              workshopDetails?.workShopDetails?.trainerShare
                            )}{" "} */}
                                <img
                                  src={primaryRiyal}
                                  width={20}
                                  height={20}
                                  alt="riyal"
                                  className="me-1"
                                />
                                {workshopDetails?.bookingDetails
                                  ?.trainerShare ?? "-"}
                                <span className="fs_12"></span>
                              </h2>
                            </div>
                          </div>
                          <div className="inner_section">
                            <div className="g_between">
                              <h2 className="fs_14 text_gray">
                                {t("Ashjar share")} (
                                {workshopDetails?.workShopDetails?.companyShare}
                                %)
                              </h2>
                              <h2 className="fs_28 text_primary">
                                <img
                                  src={primaryRiyal}
                                  width={20}
                                  height={20}
                                  alt="riyal"
                                  className="me-1"
                                />
                                {workshopDetails?.bookingDetails?.ashjarShare ??
                                  "-"}
                              </h2>
                            </div>
                          </div>
                          <div className="inner_section">
                            <div className="g_between">
                              <h2 className="fs_14 text_gray">
                                {t("Tax")} (
                                {workshopDetails?.bookingDetails?.vat}
                                %)
                              </h2>
                              <h2 className="fs_28 text_primary">
                                <img
                                  src={primaryRiyal}
                                  width={20}
                                  height={20}
                                  alt="riyal"
                                  className="me-1"
                                />
                                {workshopDetails?.bookingDetails?.vatAmount}{" "}
                              </h2>
                            </div>
                          </div>
                        </div>
                        {/* WORKSHOP BOOKINGS */}
                        <h2 className="fs_20 mt_20">
                          {t("Workshop Bookings")} (
                          <span className="text_primary">
                            {" "}
                            {workshopDetails?.bookingDetails?.seats}
                          </span>{" "}
                          / {workshopDetails?.bookingDetails?.totalSeats} )
                        </h2>
                        <div className="booking_inner_section">
                          <Table>
                            <thead className="w-100 d-table">
                              <tr>
                                <th>{t("User")}</th>
                                <th className="text-end">{t("Seats")}</th>
                                <th className="text-end">{t("Status")}</th>
                              </tr>
                            </thead>
                            <tbody className="w-100 d-table">
                              {workshopDetails?.bookingDetails?.bookings
                                ?.length === 0 ? (
                                <p className="mt-4 text-center w-100">
                                  {t("No booking found")}
                                </p>
                              ) : (
                                workshopDetails?.bookingDetails?.bookings?.map(
                                  (item) => (
                                    <tr key={item?.user?._id}>
                                      <td>
                                        <div>
                                          <Link
                                            to={`/customers/customer-information?id=${item?.user?._id}`}
                                            className="text_primary fs_16 text-decoration-underline"
                                          >
                                            {item?.user?.name || "-"}
                                          </Link>
                                          <p className="fs_14 text_gray">
                                            {item?.user?.phone || "-"}
                                          </p>
                                        </div>
                                      </td>
                                      <td>
                                        <p className="fs_14 text_black text-center">
                                          {item?.seats ?? "-"}
                                        </p>
                                      </td>
                                      <td>
                                        <p className="text-center status_tag available_status">
                                          {item?.status}
                                        </p>
                                      </td>
                                    </tr>
                                  )
                                )
                              )}
                            </tbody>
                          </Table>
                        </div>
                        {/* DOWNLOAD SECTION */}
                        <div className="g_end">
                          <button
                            className="main_btn mt-4 px-3 g_center w_fit_content gap-2"
                            onClick={handleDownloadInvoice}
                            disabled={isLoading}
                          >
                            {t("Generate Invoice")}
                            {isLoading && (
                              <Spinner
                                animation="border"
                                style={{ width: 15, height: 15 }}
                              />
                            )}
                          </button>
                        </div>
                        {!isLoading && isInvoiceGenerated && (
                          <div className="download_section mt-4">
                            <div className="g_between align-items-start">
                              <div className="d-flex gap-2">
                                <img
                                  src={PDFIcon}
                                  width={24}
                                  height={24}
                                  alt=""
                                />
                                <div>
                                  <p className="fs_14 text_black">
                                    {t(
                                      "Download Workshop Revenue Distribution & Bookings"
                                    )}
                                  </p>
                                  <span className="fs_14 size_text">
                                    {workshopInvoiceDetails?.size} KB
                                  </span>
                                </div>
                              </div>
                              <a
                                href={workshopInvoiceDetails?.invoiceUrl}
                                download={"Test.pdf"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text_gray"
                              >
                                <Download width={18} height={18} />
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="b_r_20 bg-white">
                        <NoDataFound />
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          </Container>
        )}
      </div>
    </React.Fragment>
  );
};

export default WorkshopDetails;
